// Google Places Autocomplete service
// Using the new Places API (New) REST API instead of the deprecated JavaScript SDK

interface PlacePrediction {
  description: string;
  place_id: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

interface AutocompleteResponse {
  suggestions: Array<{
    placePrediction: {
      placeId: string;
      text: {
        text: string;
        matches: Array<{
          startIndex: number;
          endIndex: number;
        }>;
      };
      structuredFormat: {
        mainText: {
          text: string;
        };
        secondaryText: {
          text: string;
        };
      };
    };
  }>;
}

// Convert new API response to old format for compatibility
const convertToLegacyFormat = (response: AutocompleteResponse): PlacePrediction[] => {
  if (!response.suggestions) {
    return [];
  }

  return response.suggestions.map((suggestion) => ({
    description: suggestion.placePrediction.text.text,
    place_id: suggestion.placePrediction.placeId,
    structured_formatting: {
      main_text: suggestion.placePrediction.structuredFormat.mainText.text,
      secondary_text: suggestion.placePrediction.structuredFormat.secondaryText.text,
    },
  }));
};

export const initPlacesService = (): boolean => {
  // No initialization needed for REST API
  return true;
};

export const getPlacePredictions = async (
  input: string,
  callback: (predictions: PlacePrediction[]) => void
): Promise<void> => {
  if (!input || input.length < 2) {
    callback([]);
    return;
  }

  const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
  if (!apiKey || apiKey === "your_google_places_api_key_here") {
    console.warn("Google Places API key not configured");
    callback([]);
    return;
  }

  try {
    // Use the new Places API (New) REST endpoint
    const response = await fetch(
      `https://places.googleapis.com/v1/places:autocomplete`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": apiKey,
          "X-Goog-FieldMask": "suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat",
        },
        body: JSON.stringify({
          input: input,
          // includedRegionCodes: [], // Optional: limit to specific regions
          // locationBias: { ... }, // Optional: bias results to a location
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.warn("Places API error:", response.status, errorData);
      callback([]);
      return;
    }

    const data: AutocompleteResponse = await response.json();
    const predictions = convertToLegacyFormat(data);
    
    if (predictions.length > 0) {
      console.log(`Found ${predictions.length} place predictions for "${input}"`);
      callback(predictions);
    } else {
      callback([]);
    }
  } catch (error: any) {
    console.warn("Places API request failed:", error?.message || error);
    callback([]);
  }
};

export const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
  // No script loading needed for REST API
  // Just verify API key is available
  return new Promise((resolve, reject) => {
    if (!apiKey || apiKey === "your_google_places_api_key_here") {
      reject(new Error("Google Places API key not configured"));
      return;
    }
    console.log("Google Places API (New) ready - using REST API");
    resolve();
  });
};

